import { CardContent, Stack } from '@mui/material';
import { Provider, type RestoreGcpDiskInput } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { GcpEncryptionComponent } from '@/components/encryption/gcpEncryptionComponent';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { SingleGcpDiskConfig } from './singleGcpDiskConfig';

import { StepContainer } from '../../../wizard/StepContainer';
import type { RestoreGcpVMState } from '../../restoreGcpVMWizard';
import { EonTagRemark } from '../eonTagRemark';

export const GcpVmDiskConfiguration = (props: StepProps<RestoreGcpVMState>) => {
  const dal = useDAL();
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const startRestore = () => {
    setIsRestoring(true);
    setError(undefined);

    const labels = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon-restore': 'true',
    };

    const diskSettings = props.wizardState.disksState.map(
      (disk): RestoreGcpDiskInput => ({
        diskId: disk.diskSettings.diskId,
        diskName: disk.diskSettings.diskName,
        diskType: disk.diskSettings.diskType,
        diskSizeBytes: disk.diskSettings.diskSizeBytes,
        restoreAccountId: props.wizardState.restoreAccount!.id,
        zone: props.wizardState.zone,
        labels: {
          ...(props.wizardState.keepOriginalTags
            ? disk.initialTags
            : disk.tags),
          'eon-restore': 'true',
        },
        encryptionKeyId: props.wizardState.encryptionKeyForAllDisks
          ? props.wizardState.encryptionKeyForAllDisks
          : disk.encryptionKey,
        iops: disk.diskSettings.iops,
        throughput: disk.diskSettings.throughput,
      })
    );

    return dal.restore.gcpVm
      .restoreVm(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        zone: props.wizardState.zone!,
        machineType: props.wizardState.machineType ?? '',
        name: props.wizardState.vmName ?? '',
        subnetName: props.wizardState.network ?? '',
        labels,
        diskSettings,
      })
      .then(() => props.finish('Restore job has started!'))
      .catch((e) => {
        setIsRestoring(false);
        setError(`Failed to restore disk: ${e.message}`);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark provider={Provider.Gcp} />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={2}
      nextButtonText='Restore'
      onBackClick={props.back}
      canGoNext={() =>
        !isRestoring &&
        !!props.wizardState.vmName &&
        !!props.wizardState.regionName &&
        !!props.wizardState.network &&
        !!props.wizardState.zone &&
        !!props.wizardState.machineType
      }
      onNextClick={startRestore}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <CardContent className='p-0'>
        <GcpEncryptionComponent
          cloudAccountId={props.wizardState.restoreAccount?.id}
          regionName={props.wizardState.regionName}
          onChange={(encryptionKeyForAllDisks) => {
            props.setWizardState((state) => ({
              ...state,
              encryptionKeyForAllDisks,
            }));
          }}
          canBeTurnedOff={true}
          title='Encrypt all disks with the same key'
          initialEncryptionKeyName={props.wizardState.encryptionKeyForAllDisks}
        />
      </CardContent>
      <CardContent className='p-0'>
        {props.wizardState.disksState.map((disk) => {
          const initialDiskSettings = props.wizardState.initialDiskStates.find(
            (d) => disk.diskSettings.diskId === d.diskSettings.diskId
          );
          if (
            !initialDiskSettings ||
            !props.wizardState.restoreAccount ||
            !props.wizardState.regionName
          ) {
            return null;
          }
          return (
            <SingleGcpDiskConfig
              key={disk.diskSettings.diskId}
              diskId={disk.diskSettings.diskId}
              accountID={props.wizardState.restoreAccount.id}
              zone={props.wizardState.zone ?? ''}
              region={props.wizardState.regionName ?? ''}
              machineType={props.wizardState.machineType}
              initialSettings={initialDiskSettings}
              diskSettings={disk}
              setWizardState={props.setWizardState}
              showEncryption={!props.wizardState.encryptionKeyForAllDisks}
            />
          );
        })}
      </CardContent>
    </StepContainer>
  );
};
